import _extends from '@babel/runtime/helpers/extends';
import React, { useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { G as Grid } from './Grid-405cf53c.js';
import { p as props, d as defaults } from './componentEl-c8b73b10.js';
import { a as addClassNames, c as classNamePropTypes } from './classNames-cc2dcbf1.js';
import IconCross from './IconCross.js';
import { C as ConditionalFocusTrap } from './ConditionalFocusTrap-43b4310b.js';
import { P as PageWrapper } from './PageWrapper-6a749c4f.js';

/**
Component to display a Modal Title
*/
function ModalTitle$1({
  componentEl: Component,
  className,
  children,
  ...otherProps
}) {
  let cssClasses = ['st_modal__title'];
  cssClasses = addClassNames(className, cssClasses);
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}
ModalTitle$1.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes
};
ModalTitle$1.defaultProps = {
  componentEl: 'h1',
  className: null
};
ModalTitle$1.displayName = 'Modal.Title';

/**
Component to display a Modal Title
*/
function ModalSubtitle({
  componentEl: Component,
  className,
  children,
  ...otherProps
}) {
  let cssClasses = ['st_modal__subtitle'];
  cssClasses = addClassNames(className, cssClasses);
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}
ModalSubtitle.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes
};
ModalSubtitle.defaultProps = {
  componentEl: 'h2',
  className: null
};
ModalSubtitle.displayName = 'Modal.Subtitle';

/**
Component to display the Modal dismiss button
*/
function ModalTitle({
  className,
  children,
  ...otherProps
}) {
  let cssClasses = ['st_modal__dismiss'];
  cssClasses = addClassNames(className, cssClasses);
  return /*#__PURE__*/React.createElement("button", _extends({
    type: "button",
    size: "md",
    className: cssClasses.join(' '),
    "aria-label": "close"
  }, otherProps), /*#__PURE__*/React.createElement(IconCross, {
    alt: "dismiss",
    className: "st_modal__dismissicon"
  }));
}
ModalTitle.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes
};
ModalTitle.defaultProps = {
  className: null
};
ModalTitle.displayName = 'Modal.Dismiss';

/**
Component to display a Modal
*/
function Modal({
  componentEl: Component,
  className,
  onDismiss,
  children,
  renderNode,
  fullWidth,
  showDismiss,
  focusTrap,
  top,
  // eslint-disable-next-line react/prop-types
  style: styleProp,
  ...otherProps
}) {
  let cssClasses = ['st_modal'];
  cssClasses = addClassNames(className, cssClasses);
  const styles = styleProp || {};
  if (top !== null) {
    styles['--st-module-modal-top'] = top;
  }

  // handle when X is clicked to manually close Modal
  const handleDismiss = useCallback(() => {
    if (onDismiss) onDismiss();
  }, [onDismiss]);
  const listenKeyboard = useCallback(e => {
    if (e.key === 'Escape' || e.keyCode === 27) {
      handleDismiss();
    }
  }, [handleDismiss]);

  // listen for Esc key
  useEffect(() => {
    window.addEventListener('keydown', listenKeyboard);
    return () => window.removeEventListener('keydown', listenKeyboard);
  }, [listenKeyboard]);
  const content = /*#__PURE__*/React.createElement(ConditionalFocusTrap, {
    focusTrap: focusTrap,
    focusTrapOptions: {
      fallbackFocus: `.${cssClasses.join('.')}`
    }
  }, /*#__PURE__*/React.createElement(Component, _extends({
    "aria-modal": "true",
    className: cssClasses.join(' '),
    style: styles
  }, otherProps, {
    tabIndex: -1
  }), showDismiss && /*#__PURE__*/React.createElement(Modal.Dismiss, {
    className: "st_modal__dismiss--placed",
    onClick: handleDismiss
  }), !fullWidth && /*#__PURE__*/React.createElement(PageWrapper, {
    className: "st_modal__content"
  }, /*#__PURE__*/React.createElement(Grid, null, /*#__PURE__*/React.createElement(Grid.Row, null, /*#__PURE__*/React.createElement(Grid.Col, {
    xs: 12,
    sm: 8,
    smOffset: 2,
    md: 6,
    mdOffset: 3
  }, children)))), fullWidth && children));
  if (!renderNode) return content;
  return /*#__PURE__*/ReactDOM.createPortal(content, renderNode);
}
Modal.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** callback when Modal is dismissed, either via the X icon, or by pressing Esc */
  onDismiss: PropTypes.func,
  /** node where modal should be rendered */
  renderNode: PropTypes.any,
  /** whether modal should go edge to edge */
  fullWidth: PropTypes.bool,
  /** whether to show dismiss button automatically */
  showDismiss: PropTypes.bool,
  /** whether to trap focus when Dialog is open, for accessibility */
  focusTrap: PropTypes.bool,
  /** css override for top position of sidebar */
  top: PropTypes.string
};
Modal.defaultProps = {
  componentEl: defaults,
  className: null,
  onDismiss: () => null,
  renderNode: null,
  fullWidth: false,
  showDismiss: true,
  focusTrap: true,
  top: null
};
Modal.displayName = 'Modal';
Modal.Title = ModalTitle$1;
Modal.Subtitle = ModalSubtitle;
Modal.Dismiss = ModalTitle;

export { Modal as M };
